<template>
  <div class="register-wrapper">
    <!-- Animierte Hintergrundelemente für zusätzlichen visuellen Effekt -->
    <div class="background-elements">
      <div class="ambient-light ambient-light-1"></div>
      <div class="ambient-light ambient-light-2"></div>
      <div class="ambient-light ambient-light-3"></div>
      <div class="grid-overlay"></div>
    </div>

    <div class="register-container">
      <v-card class="register-card" elevation="0">
        <div class="register-card-inner">
          <v-card-title
            class="register-header d-flex justify-space-between align-center"
          >
            <div class="title-container">
              <v-icon color="success" size="24" class="mr-2"
                >mdi-account-plus</v-icon
              >
              <h2 class="card-title">Registrierung</h2>
            </div>
            <v-btn
              variant="text"
              color="grey-lighten-2"
              class="return-btn"
              @click="$router.push('/')"
            >
              <v-icon class="mr-1">mdi-arrow-left</v-icon>
              Zurück
            </v-btn>
          </v-card-title>

          <v-divider class="card-divider mb-4"></v-divider>

          <v-card-text class="form-container">
            <v-text-field
              v-model="loginname"
              label="Loginname"
              variant="outlined"
              color="success"
              density="comfortable"
              prepend-inner-icon="mdi-account"
              class="form-field mb-3"
              bg-color="rgba(30, 30, 40, 0.5)"
            ></v-text-field>

            <v-text-field
              v-model="vorname"
              label="Vorname"
              variant="outlined"
              color="success"
              density="comfortable"
              prepend-inner-icon="mdi-account"
              class="form-field mb-3"
              bg-color="rgba(30, 30, 40, 0.5)"
            ></v-text-field>

            <v-text-field
              v-model="nachname"
              label="Nachname"
              variant="outlined"
              color="success"
              density="comfortable"
              prepend-inner-icon="mdi-account"
              class="form-field mb-3"
              bg-color="rgba(30, 30, 40, 0.5)"
            ></v-text-field>

            <v-text-field
              v-model="email"
              label="E-Mail"
              type="email"
              variant="outlined"
              color="success"
              density="comfortable"
              prepend-inner-icon="mdi-email"
              class="form-field mb-3"
              bg-color="rgba(30, 30, 40, 0.5)"
            ></v-text-field>

            <v-text-field
              v-model="mitarbeiternummer"
              label="Mitarbeiternummer"
              variant="outlined"
              color="success"
              density="comfortable"
              prepend-inner-icon="mdi-identifier"
              class="form-field mb-3"
              bg-color="rgba(30, 30, 40, 0.5)"
            ></v-text-field>

            <v-select
              v-model="abteilung"
              :items="abteilungen"
              label="Abteilung"
              variant="outlined"
              color="success"
              density="comfortable"
              prepend-inner-icon="mdi-domain"
              class="form-field mb-3"
              bg-color="rgba(30, 30, 40, 0.5)"
            ></v-select>

            <!-- Neues Geburtstagsfeld -->
            <v-text-field
              v-model="birthday"
              label="Geburtstag (optional)"
              type="date"
              variant="outlined"
              color="success"
              density="comfortable"
              prepend-inner-icon="mdi-cake-variant"
              class="form-field mb-3"
              bg-color="rgba(30, 30, 40, 0.5)"
              hint="Format: JJJJ-MM-TT"
              persistent-hint
            ></v-text-field>

            <!-- Checkbox für Sichtbarkeit -->
            <v-checkbox
              v-model="birthdayPublic"
              label="Geburtstag für andere Benutzer sichtbar machen"
              color="success"
              hide-details
              class="form-field mb-4"
              :disabled="!birthday"
            >
              <template v-slot:prepend>
                <v-icon color="success">mdi-eye</v-icon>
              </template>
            </v-checkbox>

            <v-text-field
              v-model="passwort"
              label="Passwort"
              type="password"
              variant="outlined"
              color="success"
              density="comfortable"
              prepend-inner-icon="mdi-lock"
              class="form-field mb-3"
              bg-color="rgba(30, 30, 40, 0.5)"
            ></v-text-field>

            <v-text-field
              v-model="passwortRepeat"
              label="Passwort wiederholen"
              type="password"
              variant="outlined"
              color="success"
              density="comfortable"
              prepend-inner-icon="mdi-lock"
              class="form-field mb-4"
              bg-color="rgba(30, 30, 40, 0.5)"
            ></v-text-field>

            <div class="d-flex justify-end">
              <v-btn
                color="success"
                class="register-btn"
                size="large"
                @click="register"
                :loading="isRegistering"
              >
                <v-icon class="mr-1">mdi-account-plus</v-icon>
                Registrieren
              </v-btn>
            </div>
          </v-card-text>
        </div>
      </v-card>
    </div>
  </div>
</template>

<script setup>
import { ref } from "vue";
import { useRouter } from "vue-router";
import axios from "axios";
import { API_BASE_URL } from "../config";
import { useToast } from "vue-toastification";
const toast = useToast();
const router = useRouter();

// Formularfelder
const loginname = ref("");
const vorname = ref("");
const nachname = ref("");
const email = ref("");
const mitarbeiternummer = ref("");
const passwort = ref("");
const passwortRepeat = ref("");
const abteilung = ref("Support"); // Standardwert, kann angepasst werden
const birthday = ref("");
const birthdayPublic = ref(false);
const isRegistering = ref(false);

// Abteilungsoptionen
const abteilungen = ref([
  "Geschäftsleitung",
  "Support",
  "KC1",
  "KC2",
  "KC3",
  "KC4",
  "Retouren",
  "Lager",
  "Wareneingang/-ausgang",
]);

const register = async () => {
  if (
    !loginname.value ||
    !vorname.value ||
    !nachname.value ||
    !email.value ||
    !mitarbeiternummer.value ||
    !passwort.value ||
    !passwortRepeat.value ||
    !abteilung.value
  ) {
    toast.error("Bitte alle Felder ausfüllen!");
    return;
  }

  if (passwort.value.length < 8) {
    toast.error("Das Passwort muss mindestens 8 Zeichen lang sein!");
    return;
  }

  if (passwort.value !== passwortRepeat.value) {
    toast.error("Die Passwörter stimmen nicht überein!");
    return;
  }

  isRegistering.value = true;

  try {
    const response = await axios.post(
      `${API_BASE_URL}register.php`,
      {
        action: "register",
        loginname: loginname.value,
        vorname: vorname.value,
        nachname: nachname.value,
        email: email.value,
        mitarbeiternummer: mitarbeiternummer.value,
        passwort: passwort.value,
        abteilung: abteilung.value,
        birthday: birthday.value,
        birthdayPublic: birthdayPublic.value,
      },
      {
        headers: { "Content-Type": "application/json" },
      }
    );

    if (response.data.status === "success") {
      toast.success("Registrierung erfolgreich! Du wirst weitergeleitet.");
      setTimeout(() => router.push("/"), 3000);
    } else {
      toast.error(response.data.message || "Registrierung fehlgeschlagen");
    }
  } catch (error) {
    toast.error("Serverfehler! Bitte versuche es später erneut.");
    console.error(error);
  } finally {
    isRegistering.value = false;
  }
};
</script>

<style scoped>
/* ======== Hintergrund & Container ======== */
.register-wrapper {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  color: rgba(255, 255, 255, 0.9);
}

/* Hintergrundelemente */
.background-elements {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  z-index: 0;
}

.ambient-light {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.15;
}

.ambient-light-1 {
  width: 600px;
  height: 600px;
  top: -200px;
  right: -100px;
  animation: pulse 15s infinite alternate;
}

.ambient-light-2 {
  width: 500px;
  height: 500px;
  bottom: -100px;
  left: -100px;
  animation: pulse 20s infinite alternate-reverse;
}

.ambient-light-3 {
  width: 400px;
  height: 400px;
  top: 40%;
  left: 50%;
  transform: translate(-50%, -50%);
  animation: pulse 18s infinite alternate;
}

@keyframes pulse {
  0% {
    opacity: 0.1;
    transform: scale(1);
  }
  50% {
    opacity: 0.15;
    transform: scale(1.05);
  }
  100% {
    opacity: 0.1;
    transform: scale(1);
  }
}

.grid-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
}

.register-container {
  width: 100%;
  max-width: 500px;
  z-index: 10;
  padding: 20px;
  position: relative;
  animation: fadeIn 0.8s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ======== Registerkarte ======== */
.register-card {
  width: 100%;
  background: rgba(25, 25, 30, 0.85) !important;
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  border-radius: 18px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  transform-style: preserve-3d;
  perspective: 1000px;
}

.register-card-inner {
  height: 100%;
}

/* ======== Header und Titel ======== */
.register-header {
  padding: 8px 20px;

  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  text-align: left;
}

.title-container {
  display: flex;
  align-items: center;
}

.card-title {
  font-size: 18px;
  font-weight: 500;
  margin: 0;
  letter-spacing: 0.3px;
  -webkit-background-clip: text;
  background-clip: text;
}

.card-divider {
  border-color: rgba(255, 255, 255, 0.06);
  margin: 0;
}

.return-btn {
  transition: all 0.2s ease;
  opacity: 0.85;
  border-radius: 8px;
}

.return-btn:hover {
  opacity: 1;
  transform: translateX(-2px);
  background: rgba(255, 255, 255, 0.05);
}

/* ======== Formularelemente ======== */
.form-container {
  padding: 20px;
}

.form-field {
  margin-bottom: 16px;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.form-field :deep(.v-field__input) {
  color: rgba(255, 255, 255, 0.9);
}

.form-field :deep(.v-field__outline) {
  color: rgba(255, 255, 255, 0.12);
}

.form-field:hover :deep(.v-field__outline) {
  color: rgba(255, 255, 255, 0.18);
}

.form-field :deep(.v-field__prepend-inner .v-icon) {
  color: rgba(76, 175, 80, 0.9) !important;
}

/* ======== Registrieren-Button ======== */
.register-btn {
  height: 44px;
  border-radius: 12px !important;
  font-weight: 500;
  letter-spacing: 0.5px;
  text-transform: none;
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3) !important;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  min-width: 180px;
}

.register-btn::before {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  transition: 0.6s;
}

.register-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 15px rgba(76, 175, 80, 0.4) !important;
}

.register-btn:hover::before {
  left: 100%;
}

/* ======== Responsive Design ======== */
@media (max-width: 600px) {
  .register-container {
    padding: 15px;
  }

  .register-header {
    padding: 14px 16px;
  }

  .card-title {
    font-size: 16px;
  }

  .form-container {
    padding: 16px;
  }
}
</style>
